import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Eye, EyeOff, KeyRound, Shield, CheckCircle2 } from 'lucide-react'
import {
  DraggableDialog,
  DraggableDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/draggable-dialog'

interface ChangePasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>
  isLoading?: boolean
}

export function ChangePasswordDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false
}: ChangePasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  // Validación en tiempo real
  const validatePassword = (pwd: string) => {
    const errors: string[] = []
    if (pwd.length < 8) errors.push('Mínimo 8 caracteres')
    if (!/[A-Z]/.test(pwd)) errors.push('Al menos 1 mayúscula')
    if (!/[a-z]/.test(pwd)) errors.push('Al menos 1 minúscula')
    if (!/[0-9]/.test(pwd)) errors.push('Al menos 1 número')
    if (!/[^A-Za-z0-9]/.test(pwd)) errors.push('Al menos 1 carácter especial')
    return errors
  }

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value)
    setErrors(validatePassword(value))
  }

  const handleSubmit = async () => {
    // Validar que todos los campos estén llenos
    if (!currentPassword || !newPassword || !confirmPassword) {
      return
    }

    // Validar que las contraseñas nuevas coincidan
    if (newPassword !== confirmPassword) {
      return
    }

    // Validar fortaleza de la nueva contraseña
    const validationErrors = validatePassword(newPassword)
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      await onSubmit(currentPassword, newPassword)
      // Limpiar formulario después del éxito
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setErrors([])
    } catch (error) {
      // El error será manejado por el componente padre
      console.error('Failed to change password:', error)
    }
  }

  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0
  const isValid =
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    passwordsMatch &&
    errors.length === 0

  return (
    <DraggableDialog open={open} onOpenChange={onOpenChange}>
      <DraggableDialogContent className="flex flex-col sm:max-w-[600px] max-h-[85vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-background">
          <DialogHeader className="text-left space-y-3">
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" /> Cambiar Mi Contraseña
            </DialogTitle>
            <DialogDescription>
              Ingresa tu contraseña actual y define una nueva contraseña segura
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4" onWheelCapture={(e) => e.stopPropagation()}>
          <div className="space-y-4">
            {/* Current Password */}
            <div className="space-y-2">
              <Label htmlFor="current-password">Contraseña Actual</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Ingresa tu contraseña actual"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="new-password">Nueva Contraseña</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => handleNewPasswordChange(e.target.value)}
                  placeholder="Ingresa tu nueva contraseña"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmar Nueva Contraseña</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirma tu nueva contraseña"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {/* Password Match Indicator */}
              {confirmPassword.length > 0 && (
                <div className={`flex items-center gap-1 text-xs ${passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                  {passwordsMatch ? (
                    <>
                      <CheckCircle2 className="h-3 w-3" />
                      <span>Las contraseñas coinciden</span>
                    </>
                  ) : (
                    <span>Las contraseñas no coinciden</span>
                  )}
                </div>
              )}
            </div>

            {/* Password Requirements */}
            {newPassword.length > 0 && (
              <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
                <div className="space-y-1.5">
                  <p className="text-xs font-medium flex items-center gap-2">
                    <Shield className="h-3.5 w-3.5" /> Requisitos de seguridad:
                  </p>
                  <ul className="space-y-1 text-xs">
                    <li className={newPassword.length >= 8 ? 'text-green-600' : 'text-muted-foreground'}>
                      {newPassword.length >= 8 ? '✅' : '○'} Mínimo 8 caracteres
                    </li>
                    <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : 'text-muted-foreground'}>
                      {/[A-Z]/.test(newPassword) ? '✅' : '○'} Al menos 1 mayúscula
                    </li>
                    <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : 'text-muted-foreground'}>
                      {/[a-z]/.test(newPassword) ? '✅' : '○'} Al menos 1 minúscula
                    </li>
                    <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : 'text-muted-foreground'}>
                      {/[0-9]/.test(newPassword) ? '✅' : '○'} Al menos 1 número
                    </li>
                    <li className={/[^A-Za-z0-9]/.test(newPassword) ? 'text-green-600' : 'text-muted-foreground'}>
                      {/[^A-Za-z0-9]/.test(newPassword) ? '✅' : '○'} Al menos 1 carácter especial
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t bg-background px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isLoading}>
            {isLoading ? 'Cambiando...' : 'Cambiar Contraseña'}
          </Button>
        </div>
      </DraggableDialogContent>
    </DraggableDialog>
  )
}
